import { useState } from "react";

import { api, clinicHeader, readError } from "../api/client";
import { ErrorNote } from "./ErrorNote";

// The contract types this as an enum, so the draft state carries the same
// type rather than a loose string that would need casting at the call.
type IdType = "nric" | "passport" | "other" | "";

const TYPE_LABELS: Record<string, string> = {
  nric: "NRIC",
  passport: "Passport",
  other: "Other",
};

/**
 * The number off the patient's card.
 *
 * This is how a Malaysian clinic actually finds somebody, so it sits at the
 * top of the chart with the name rather than inside a settings screen.
 *
 * Only the front desk and a clinic administrator may change it. The ADR-008
 * matrix gives demographics W to them and R to a doctor and a nurse, so a
 * doctor who spots a wrong number asks the front desk, which is how the clinic
 * already works. The server enforces that; the button being absent is a
 * convenience.
 *
 * Separators come off on the way in, so 900101-14-5523 and 900101145523 are
 * the same patient. Nothing here validates the shape: passports and foreign
 * documents exist, and refusing a real card is worse than storing an odd
 * string.
 */
export function IdentifierField({
  patientId,
  type,
  value,
  canEdit,
  onChanged,
}: {
  patientId: string;
  type: string;
  value: string;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draftType, setDraftType] = useState<IdType>((type as IdType) || "nric");
  const [draftValue, setDraftValue] = useState(value ?? "");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    const { error: failed } = await api.PATCH("/portal/patients/{id}/identifier", {
      params: { path: { id: patientId }, header: clinicHeader() },
      body: { identifier_type: draftValue.trim() ? draftType : "", identifier_value: draftValue.trim() },
    });
    setSaving(false);
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    setError(null);
    setEditing(false);
    onChanged();
  };

  if (editing) {
    return (
      <form className="identifier-form" onSubmit={save}>
        <select value={draftType} onChange={(e) => setDraftType(e.target.value as IdType)}>
          <option value="nric">NRIC</option>
          <option value="passport">Passport</option>
          <option value="other">Other</option>
        </select>
        <input
          value={draftValue}
          onChange={(e) => setDraftValue(e.target.value)}
          placeholder="900101-14-5523"
          autoFocus
        />
        <button type="submit" disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </button>
        <button type="button" onClick={() => setEditing(false)}>
          Cancel
        </button>
        <ErrorNote message={error} />
      </form>
    );
  }

  return (
    <span className="identifier">
      {value ? (
        <>
          <span className="identifier-type">{TYPE_LABELS[type] ?? "ID"}</span>
          <span className="mono">{value}</span>
        </>
      ) : (
        <span className="muted">No identity number on record</span>
      )}
      {canEdit && (
        <button type="button" className="linklike" onClick={() => setEditing(true)}>
          {value ? "Change" : "Add one"}
        </button>
      )}
    </span>
  );
}
